import i18next from 'i18next';
import * as yup from 'yup';

import { UseCaseTableFields } from 'core/enums/common';

const nameValidation = /^[a-zA-Z\u0600-\u06FF]+$/;
// At least 1 uppercase letter
const upperCaseRegex = /^(?=.*[A-Z])/;
// At least 1 lowercase letter
const lowerCaseRegex = /^(?=.*[a-z])/;
// At least 1 number
const numberRegex = /^(?=.*\d)/;
// At least 1 symbol (special character)
const symbolRegex = /^(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/;
const textValidation = /^[\u0600-\u06FF\p{L}\p{N}\p{P}\p{Z}\s]*$/u;
const emailRegex =
	// eslint-disable-next-line no-useless-escape
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})|(([a-zA-Z0-9]+[-a-zA-Z0-9]*[a-zA-Z0-9]+\.)+[a-zA-Z]{2,}))$/;

export const loginSchema = () =>
	yup.object().shape({
		email: yup
			.string()
			.required(i18next.t('loginForm.requiredEmail', { ns: 'yupSchema' }) as string)
			.matches(emailRegex, i18next.t('yupSchema:signUpForm.emailValidated') as string),
		password: yup.string().required(i18next.t('yupSchema:loginForm.requiredPassword') as string),
	});

export const signUpSchema = () =>
	yup.object().shape({
		firstName: yup
			.string()
			.trim()
			.max(50, (param) => i18next.t('yupSchema:signUpForm.firstNameMaxLength', { max: param.max }))
			.required(i18next.t('yupSchema:signUpForm.firstNameRequired') as string)
			.matches(nameValidation, i18next.t('yupSchema:signUpForm.validationError') as string),
		organization: yup
			.string()
			.max(100, (param) =>
				i18next.t('yupSchema:signUpForm.organizationMaxLength', { max: param.max })
			)
			.nullable(),
		lastName: yup
			.string()
			.trim()
			.max(50, (param) => i18next.t('yupSchema:signUpForm.lastNameMaxLength', { max: param.max }))
			.required(i18next.t('yupSchema:signUpForm.lastNameRequired') as string)
			.matches(nameValidation, i18next.t('yupSchema:signUpForm.validationError') as string),
		email: yup
			.string()
			.trim()
			.max(200, (param) => i18next.t('yupSchema:signUpForm.emailMaxLength', { max: param.max }))
			.required(i18next.t('yupSchema:signUpForm.emailRequired') as string)
			.email(i18next.t('yupSchema:signUpForm.emailValidated') as string),
		department: yup
			.string()
			.max(100, (param) =>
				i18next.t('yupSchema:signUpForm.departmentMaxLength', { max: param.max })
			)
			.nullable(),
		password: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:signUpForm.passwordRequired') as string)
			.min(12, i18next.t('yupSchema:signUpForm.passwordMinimumLength') as string)
			.max(100, (param) => i18next.t('yupSchema:signUpForm.passwordMaxLength', { max: param.max }))
			.matches(upperCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOneUpperCase') as string)
			.matches(lowerCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOnLowerCase') as string)
			.matches(numberRegex, i18next.t('yupSchema:signUpForm.atLeastOneNumber') as string)
			.matches(symbolRegex, i18next.t('yupSchema:signUpForm.atLeastOneSymbol') as string),

		// phoneNumber: yup.string().trim().required('Phone Number is required'),
		phoneNumber: yup
			.string()
			.max(20, (param) => i18next.t('yupSchema:signUpForm.phoneMaxLength', { max: param.max }))
			.nullable(),
		confirmPassword: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:signUpForm.confirmPasswordRequired') as string)
			.test(
				'passwords-match',
				i18next.t('yupSchema:signUpForm.confirmPasswordNotMatch') as string,
				function (value) {
					return this.parent.password === value || value === undefined;
				}
			),
		check: yup
			.bool()
			.oneOf([true], i18next.t('yupSchema:signUpForm.termsAndConditionsRequired') as string),
	});

export const forgotPasswordSchema = () =>
	yup.object().shape({
		email: yup
			.string()
			.required(i18next.t('yupSchema:forgotPasswordForm.emailRequired') as string)
			.email(i18next.t('yupSchema:forgotPasswordForm.emailValidated') as string)
			.matches(emailRegex, i18next.t('yupSchema:signUpForm.emailValidated') as string),
	});

export const resetPasswordSchema = () =>
	yup.object().shape({
		password: yup
			.string()
			.min(12, i18next.t('yupSchema:signUpForm.passwordMinimumLength') as string)
			.matches(upperCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOneUpperCase') as string)
			.matches(lowerCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOnLowerCase') as string)
			.matches(numberRegex, i18next.t('yupSchema:signUpForm.atLeastOneNumber') as string)
			.matches(symbolRegex, i18next.t('yupSchema:signUpForm.atLeastOneSymbol') as string)
			.required(i18next.t('yupSchema:resetPasswordForm.passwordRequired') as string),
		confirmPassword: yup
			.string()
			.required(i18next.t('yupSchema:resetPasswordForm.passwordValidation') as string)
			.test(
				'passwords-match',
				i18next.t('yupSchema:resetPasswordForm.confirmPasswordRequired') as string,
				function (value) {
					return this.parent.password === value || value === undefined;
				}
			),
	});

export const editUserStoriesSchema = () =>
	yup.object().shape({
		description: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:editUserStories.descriptionRequired') as string)
			.matches(
				textValidation,
				i18next.t('yupSchema:editUserStories.descriptionValidation') as string
			),
		ticketSummaries: yup
			.array()
			.required()
			.of(
				yup.object().shape({
					data: yup
						.string()
						.trim()
						.required(i18next.t('yupSchema:editUseCases.diagrams.titleRequired') as string)
						.matches(
							textValidation,
							i18next.t('yupSchema:editUserStories.userStoriesValidation') as string
						),
					summaryScenarios: yup.array().of(
						yup.object().shape({
							title: yup
								.string()
								.trim()
								.required(
									i18next.t(
										'yupSchema:editUserStories.diagrams.acceptanceCriteriaDescriptionValidation'
									) as string
								)
								.matches(
									textValidation,
									i18next.t('yupSchema:editUserStories.scenarioTitleValidation') as string
								),
							description: yup
								.string()
								.trim()
								.required(
									i18next.t(
										'yupSchema:editUserStories.diagrams.acceptanceCriteriaDescriptionValidation'
									) as string
								)
								.matches(
									textValidation,
									i18next.t('yupSchema:editUserStories.scenarioDescriptionValidation') as string
								),
							id: yup.string(),
							isDeleted: yup.boolean().required(),
						})
					),
					summaryAcceptanceCriteria: yup.array().of(
						yup.object().shape({
							title: yup
								.string()
								.trim()
								.required(
									i18next.t(
										'yupSchema:editUserStories.diagrams.acceptanceCriteriaDescriptionValidation'
									) as string
								)
								.matches(
									textValidation,
									i18next.t('yupSchema:editUserStories.acceptanceCriteriaTitleValidation') as string
								),
							description: yup
								.string()
								.trim()
								.required(
									i18next.t(
										'yupSchema:editUserStories.diagrams.acceptanceCriteriaDescriptionValidation'
									) as string
								)
								.matches(
									textValidation,
									i18next.t(
										'yupSchema:editUserStories.acceptanceCriteriaDescriptionValidation'
									) as string
								),
							isDeleted: yup.boolean().required(),
							id: yup.string(),
						})
					),
					isDeleted: yup.boolean().required(),
					id: yup.string(),
				})
			),
	});

export const editUseCasesSchema = () =>
	yup.object().shape({
		title: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:editUseCases.title.required') as string)
			.matches(textValidation, i18next.t('yupSchema:editUseCases.title.validation') as string),
		diagrams: yup
			.array()
			.required()
			.of(
				yup.object().shape({
					title: yup
						.string()
						.trim()
						.test(
							'is not empty',
							i18next.t('yupSchema:editUseCases.diagrams.titleRequired') as string,
							(val) => !!val?.length
						)
						.matches(
							textValidation,
							i18next.t('yupSchema:editUseCases.diagrams.titleValidation') as string
						),
					description: yup.string(),

					code: yup.string(),
					pictureLink: yup.string(),
				})
			),
		tables: yup
			.array()
			.required()
			.of(
				yup.object().shape({
					table: yup.object().shape({
						[UseCaseTableFields[UseCaseTableFields['Key Roles']]]: yup
							.array()
							.required()
							.of(
								yup.object().shape({
									Role: yup.object().shape({
										value: yup
											.string()
											.required(i18next.t('yupSchema:editUseCases.table.required') as string)
											.matches(
												textValidation,
												i18next.t('yupSchema:editUseCases.table.validation') as string
											),
										isDeleted: yup.boolean().required(),
									}),
									Responsibilities: yup.object().shape({
										value: yup
											.string()
											.required(i18next.t('yupSchema:editUseCases.table.required') as string)
											.matches(
												textValidation,
												i18next.t('yupSchema:editUseCases.table.validation') as string
											),
										isDeleted: yup.boolean().required(),
									}),
								})
							),
						[UseCaseTableFields[UseCaseTableFields['Preconditions']]]: yup
							.array()
							.required()
							.of(
								yup.object().shape({
									value: yup
										.string()
										.required(i18next.t('yupSchema:editUseCases.table.required') as string)
										.matches(
											textValidation,
											i18next.t('yupSchema:editUseCases.table.validation') as string
										),
									isDeleted: yup.boolean().required(),
								})
							),
						[UseCaseTableFields[UseCaseTableFields['Trigger']]]: yup
							.array()
							.required()
							.of(
								yup.object().shape({
									value: yup
										.string()
										.required(i18next.t('yupSchema:editUseCases.table.required') as string)
										.matches(
											textValidation,
											i18next.t('yupSchema:editUseCases.table.validation') as string
										),
									isDeleted: yup.boolean().required(),
								})
							),
						[UseCaseTableFields[UseCaseTableFields['Successful Outcome']]]: yup
							.array()
							.required()
							.of(
								yup.object().shape({
									value: yup
										.string()
										.required(i18next.t('yupSchema:editUseCases.table.required') as string)
										.matches(
											textValidation,
											i18next.t('yupSchema:editUseCases.table.validation') as string
										),
									isDeleted: yup.boolean().required(),
								})
							),
						[UseCaseTableFields[UseCaseTableFields['Main Scenario Flow of Events']]]: yup
							.array()
							.required()
							.of(
								yup.object().shape({
									value: yup
										.string()
										.required(i18next.t('yupSchema:editUseCases.table.required') as string)
										.matches(
											textValidation,
											i18next.t('yupSchema:editUseCases.table.validation') as string
										),
									isDeleted: yup.boolean().required(),
								})
							),
					}),
				})
			),
	});

export const editTestCasesSchema = () =>
	yup.object().shape({
		userStoryTests: yup
			.array()
			.required()
			.of(
				yup.object().shape({
					useCaseTitle: yup.string().required('Test').trim().matches(textValidation, 'Test'),
					testPlan: yup.object().shape({
						objective: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						scope: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						resources: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						schedule: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						testEnvironment: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						riskManagement: yup.string().required('Test').trim().matches(textValidation, 'Test'),
						id: yup.string().nullable(),
					}),
					testScenarios: yup
						.array()
						.of(
							yup.object().shape({
								data: yup.string().required('Test').trim().matches(textValidation, 'Test'),
								isDeleted: yup.boolean().required(),
							})
						)
						.required('Test'),
					testCases: yup
						.array()
						.required('Test')
						.of(
							yup.object().shape({
								testCaseId: yup.string().required('Test').trim().matches(textValidation, 'Test'),
								description: yup.string().required('Test').trim().matches(textValidation, 'Test'),
								preConditions: yup.string().required('Test').trim().matches(textValidation, 'Test'),
								testSteps: yup
									.array()
									.of(
										yup.object().shape({
											data: yup.string().required('Test').trim().matches(textValidation, 'Test'),
											isDeleted: yup.boolean().required(),
										})
									)
									.required('Test'),
								testData: yup.string().required('Test').trim().matches(textValidation, 'Test'),
								expectedResult: yup
									.string()
									.required('Test')
									.trim()
									.matches(textValidation, 'Test'),
								isDeleted: yup.boolean().required(),
							})
						),
				})
			),
	});

export const editNotificationsSchema = () =>
	yup.object().shape({
		notifications: yup
			.array()
			.required()
			.of(
				yup.object().shape({
					title: yup
						.string()
						.trim()
						.test(
							'is not empty',
							i18next.t('yupSchema:editUseCases.diagrams.titleRequired') as string,
							(val) => !!val?.length
						)
						.matches(
							textValidation,
							i18next.t('yupSchema:editNotifications.title.validation') as string
						),
					description: yup
						.string()
						.trim()
						.test(
							'is not empty',
							i18next.t('yupSchema:editUseCases.diagrams.titleRequired') as string,
							(val) => !!val?.length
						)
						.matches(
							textValidation,
							i18next.t('yupSchema:editNotifications.description.validation') as string
						),
					sms: yup.boolean().required(),
					email: yup.boolean().required(),
					push: yup.boolean().required(),
					isDeleted: yup.boolean().required(),
				})
			),
	});

export const editTitleSchema = () =>
	yup.object().shape({
		title: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:editTitle.required') as string)
			.matches(textValidation, i18next.t('yupSchema:editTitle.validation') as string),
	});

export const userProfileSchema = () =>
	yup.object().shape({
		firstName: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:signUpForm.firstNameRequired') as string)
			.max(50, (param) => i18next.t('yupSchema:signUpForm.firstNameMaxLength', { max: param.max }))
			.matches(nameValidation, i18next.t('yupSchema:signUpForm.validationError') as string),
		lastName: yup
			.string()
			.trim()
			.required(i18next.t('yupSchema:signUpForm.lastNameRequired') as string)
			.max(50, (param) => i18next.t('yupSchema:signUpForm.lastNameMaxLength', { max: param.max }))
			.matches(nameValidation, i18next.t('yupSchema:signUpForm.validationError') as string),
		organization: yup
			.string()
			.max(100, (param) =>
				i18next.t('yupSchema:signUpForm.organizationMaxLength', { max: param.max })
			)
			.nullable(),
		department: yup
			.string()
			.max(100, (param) =>
				i18next.t('yupSchema:signUpForm.departmentMaxLength', { max: param.max })
			)
			.nullable(),
		phoneNumber: yup
			.string()
			.max(20, (param) => i18next.t('yupSchema:signUpForm.phoneMaxLength', { max: param.max }))
			.nullable(),
	});

export const userChangePassword = () =>
	yup.object().shape({
		oldPassword: yup
			.string()
			.max(100, (param) =>
				i18next.t('yupSchema:changePassword.oldPasswordMaxLength', { max: param.max })
			)
			.required(i18next.t('yupSchema:changePassword.oldPasswordRequired') as string),
		newPassword: yup
			.string()
			.min(12, i18next.t('yupSchema:signUpForm.passwordMinimumLength') as string)
			.matches(upperCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOneUpperCase') as string)
			.matches(lowerCaseRegex, i18next.t('yupSchema:signUpForm.atLeastOnLowerCase') as string)
			.matches(numberRegex, i18next.t('yupSchema:signUpForm.atLeastOneNumber') as string)
			.matches(symbolRegex, i18next.t('yupSchema:signUpForm.atLeastOneSymbol') as string)
			.max(100, (param) =>
				i18next.t('yupSchema:changePassword.newPasswordMaxLength', { max: param.max })
			)
			.required(i18next.t('yupSchema:changePassword.newPasswordRequired') as string),
		confirmNewPassword: yup
			.string()
			.required(i18next.t('yupSchema:changePassword.confirmNewPasswordRequired') as string)
			.test(
				'passwords-match',
				i18next.t('yupSchema:changePassword.confirmNewPasswordNotMatch') as string,
				function (value) {
					return this.parent.newPassword === value || value === undefined;
				}
			),
	});

export const userChangeEmail = () =>
	yup.object().shape({
		email: yup
			.string()
			.trim()
			.max(200, (param) => i18next.t('yupSchema:signUpForm.emailMaxLength', { max: param.max }))
			.required(i18next.t('yupSchema:signUpForm.emailRequired') as string)
			.email(i18next.t('yupSchema:signUpForm.emailValidated') as string)
			.matches(emailRegex, i18next.t('yupSchema:signUpForm.emailValidated') as string),
	});
